import React, { useState, useEffect, useCallback } from "react";
import { Tag, Table, Collapse, Divider } from "antd";
import { Column } from "@ant-design/charts";
import { useTranslation } from "react-i18next";
import moment from "moment";

function LabelsReports({ selected, chartsRange, data }) {
    const { Panel } = Collapse;
    const { t } = useTranslation();
    const [totalDataSource, setTotalDataSource] = useState([]);
    const [dwellTimeDataSource, setDwellTimeDataSource] = useState([]);
    const [dwellTimeInOfficeHoursDataSource, setDwellTimeInOfficeHoursDataSource] = useState([]);

    const [tableColumns, setTableColumns] = useState([
        {
            title: "Name",
            dataIndex: "name",
            key: "name",
        },
    ]);

    const [totalLineChartData, setTotalLineChartData] = useState([]);
    const [dwellTimeInOfficeHoursLineChartData, setDwellTimeInOfficeHoursLineChartData] = useState(
        []
    );
    const [dwellTimeLineChartData, setDwellTimeLineChartData] = useState([]);

    const getLabelTitleById = useCallback(
        (id) => {
            let currentLabel = selected.find((label) => parseInt(label.id) === parseInt(id));
            if (currentLabel && currentLabel.value) {
                return currentLabel.value;
            }
            return id;
        },
        [selected]
    );

    const getTotalTableCols = useCallback(
        (colsRange) => {
            let newCols = [
                {
                    title: t("name"),
                    dataIndex: "name",
                    key: "name",
                },
            ];
            colsRange.forEach((date) => {
                newCols.push({
                    title: date,
                    dataIndex: "date" + date,
                    key: "date" + date,
                });
            });
            return newCols;
        },
        [t]
    );

    const getTotalTableRows = useCallback(
        (tableRowData, dataPointProp) => {
            let rows = [];
            if (!tableRowData.conversationLabelsStats) {
                return [];
            }
            tableRowData.conversationLabelsStats.forEach((label) => {
                let rowToAdd = { name: getLabelTitleById(label.conversationLabelsId) };
                chartsRange.forEach((rangeDate) => {
                    let dataPoint = label.dataPoints.find(
                        (dataPoint) =>
                            moment(dataPoint.date).utc().format("DD/MM/YYYY") === rangeDate
                    );
                    if (dataPoint) {
                        rowToAdd["date" + rangeDate] = dataPoint[dataPointProp];
                    } else {
                        rowToAdd["date" + rangeDate] = "-";
                    }
                });
                rows.push(rowToAdd);
            });
            return rows;
        },
        [chartsRange, getLabelTitleById]
    );

    let totalConfig = {
        data: totalLineChartData,
        xField: "date",
        yField: "total",
        seriesField: "name",
        isGroup: true,
    };

    let dwellTimeConfig = {
        data: dwellTimeLineChartData,
        xField: "date",
        yField: "total",
        seriesField: "name",
        isGroup: true,
    };

    let dwellTimeInOfficeHoursConfig = {
        data: dwellTimeInOfficeHoursLineChartData,
        xField: "date",
        yField: "total",
        seriesField: "name",
        isGroup: true,
    };

    const getLineChartData = useCallback(
        (dates, data, dataProp) => {
            if (!data.conversationLabelsStats) {
                return;
            }
            let chartLines = [];
            data.conversationLabelsStats.forEach((label) => {
                let lineToAdd = {
                    name: getLabelTitleById(label.conversationLabelsId),
                    id: label.conversationLabelsId,
                };
                dates.forEach((date) => {
                    lineToAdd["date"] = date;
                    let dataPoint = label.dataPoints.find(
                        (dataPoint) => moment(dataPoint.date).utc().format("DD/MM/YYYY") === date
                    );
                    if (dataPoint) {
                        lineToAdd["total"] = dataPoint[dataProp];
                    } else {
                        lineToAdd["total"] = 0;
                    }
                    chartLines.push({ ...lineToAdd });
                });
            });
            return chartLines;
        },
        [getLabelTitleById]
    );

    useEffect(() => {
        let cols = getTotalTableCols(chartsRange);
        let totalRows = getTotalTableRows(data, "amountConversationsLabeled");
        let dwellTimeRows = getTotalTableRows(data, "meanDwellTime");
        let dwellTimeInOfficeHoursRows = getTotalTableRows(data, "meanInOfficeHoursDwellTime");

        let totalChartData = getLineChartData(chartsRange, data, "amountConversationsLabeled");
        let dwellTimeChartData = getLineChartData(chartsRange, data, "meanDwellTime");
        let dwellTimeInOfficeHoursChartData = getLineChartData(
            chartsRange,
            data,
            "meanInOfficeHoursDwellTime"
        );

        setTotalLineChartData(totalChartData);
        setDwellTimeLineChartData(dwellTimeChartData);
        setDwellTimeInOfficeHoursLineChartData(dwellTimeInOfficeHoursChartData);

        setTableColumns(cols);

        setTotalDataSource(totalRows);
        setDwellTimeDataSource(dwellTimeRows);
        setDwellTimeInOfficeHoursDataSource(dwellTimeInOfficeHoursRows);
    }, [chartsRange, data, getLineChartData, getTotalTableRows, getTotalTableCols]);

    return (
        <>
            <div style={{ display: "flex", justifyContent: "flex-start" }}>
                <p className="opt-title" style={{ textAlign: "left" }}>
                    <span style={{ marginRight: "6px" }}>
                        {t("labelsReports.conversationTags")}
                    </span>
                </p>
            </div>
            {selected.map((item) => (
                <Tag color="blue">{item.value}</Tag>
            ))}
            <p className="opt-title" style={{ textAlign: "left", marginTop: "20px" }}>
                <span style={{ marginRight: "6px" }}>{t("labelsReports.totalUses")} </span>
            </p>
            <Table
                columns={tableColumns}
                dataSource={totalDataSource}
                style={{ overflowY: "auto", maxHeight: "600px", overflowX: "auto" }}
                pagination={false}
            />
            <Collapse ghost>
                <Panel header={t("reports.viewChart")} key="1">
                    {<Column {...totalConfig} />}
                </Panel>
            </Collapse>
            <Divider />

            <p className="opt-title" style={{ textAlign: "left", marginTop: "20px" }}>
                <span style={{ marginRight: "6px" }}> {t("labelsReports.meanDwellTime")} </span>
            </p>
            <Table
                columns={tableColumns}
                dataSource={dwellTimeDataSource}
                style={{ overflowY: "auto", maxHeight: "600px", overflowX: "auto" }}
                pagination={false}
            />
            <Collapse ghost>
                <Panel header={t("reports.viewChart")} key="1">
                    {<Column {...dwellTimeConfig} />}
                </Panel>
            </Collapse>
            <Divider />

            <p className="opt-title" style={{ textAlign: "left", marginTop: "20px" }}>
                <span style={{ marginRight: "6px" }}>
                    {" "}
                    {t("labelsReports.meanDwellTimeInOfficeHours")}{" "}
                </span>
            </p>
            <Table
                columns={tableColumns}
                dataSource={dwellTimeInOfficeHoursDataSource}
                style={{ overflowY: "auto", maxHeight: "600px", overflowX: "auto" }}
                pagination={false}
            />
            <Collapse ghost>
                <Panel header={t("reports.viewChart")} key="1">
                    {<Column {...dwellTimeInOfficeHoursConfig} />}
                </Panel>
            </Collapse>
            <Divider />
        </>
    );
}
export default LabelsReports;
